import App from "./App";
import { createRoot } from "react-dom/client";
import React from "react"; // Add React import for JSX

declare global {
  interface Window {
    AheadBooking?: {
      init: (selector: string) => void;
    };
    embeddedAppRoots?: Map<string, any>;
    appLoaded?: boolean;
    isInitializing?: boolean; // Add flag to track initialization state
  }
}

// Initialize tracking of embedded roots
window.embeddedAppRoots = window.embeddedAppRoots || new Map();

// Enhanced error logging function
const logError = (source: string, error: any) => {
  console.error(`Error in embedded widget (${source}):`, error);

  // Create a more detailed error object for debugging
  const errorDetails = {
    message: error?.message || String(error),
    stack: error?.stack,
    timestamp: new Date().toISOString(),
    source,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  console.error("Error details:", errorDetails);
};

// Helper function to display error message in embedded container
const showEmbedError = (container: Element, message: string) => {
  const errorDiv = document.createElement("div");
  errorDiv.className = "p-4 bg-red-50 text-red-700 rounded-md";
  errorDiv.textContent =
    message || "An error occurred while loading the booking widget.";

  // Clear container
  container.innerHTML = "";
  container.appendChild(errorDiv);
};

// Global initialization lock to prevent concurrent/repeated initializations
// This is especially important on mobile where events can fire multiple times
window.isInitializing = false;

const init = (selector: string) => {
  // Add debounce to prevent rapid multiple initializations
  if (window.isInitializing) {
    console.log("Initialization already in progress, skipping...");
    return;
  }

  // Set global initialization lock
  window.isInitializing = true;

  // Release lock after a timeout in case of errors
  const initTimeout = setTimeout(() => {
    window.isInitializing = false;
  }, 5000);

  try {
    console.log(
      "Attempting to initialize AheadBooking widget with selector:",
      selector,
    );

    // Guard against selector not found
    const container = document.querySelector(selector);
    if (!container) {
      console.error(`No element found with selector: ${selector}`);
      window.isInitializing = false;
      clearTimeout(initTimeout);
      return;
    }

    // Check if this container already has a root
    if (window.embeddedAppRoots && window.embeddedAppRoots.has(selector)) {
      console.log(
        `Widget already initialized for ${selector}, updating instead of creating new instance`,
      );
      const existingRoot = window.embeddedAppRoots.get(selector);
      try {
        existingRoot.render(React.createElement(App));
        window.isInitializing = false;
        clearTimeout(initTimeout);
        return;
      } catch (error) {
        logError("Root rerender", error);
        // If rerendering fails, proceed with new initialization
        window.embeddedAppRoots.delete(selector);
      }
    }

    // Create a new div for React to render into
    container.innerHTML = ""; // Clear existing content
    const mountPoint = document.createElement("div");
    mountPoint.style.width = "100%";
    mountPoint.style.height = "100%";
    container.appendChild(mountPoint);

    // Add global error handler for this instance
    const componentErrorHandler = (event: ErrorEvent) => {
      // Skip script errors without details
      if (
        event.message === "Script error." ||
        event.message === "Script error"
      ) {
        console.warn(
          "Cross-origin script error detected in embed, continuing execution",
        );
        event.preventDefault();
        return true;
      }

      // Only handle errors from this component
      if (
        container.contains(event.target as Node) ||
        !event.filename || // Handle script errors too
        event.filename.includes("embed.ts")
      ) {
        logError("widget render", {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
        });

        showEmbedError(
          container,
          "An error occurred while loading the booking widget. Please try refreshing the page.",
        );
        event.preventDefault();
        return true;
      }
      return false;
    };

    // Use capture to ensure we handle errors before they propagate
    window.addEventListener("error", componentErrorHandler, true);

    // Create a safer render method that clears the initialization lock when done
    const safeRender = () => {
      try {
        const root = createRoot(mountPoint);
        root.render(React.createElement(App));

        // Store reference to root
        if (window.embeddedAppRoots) {
          window.embeddedAppRoots.set(selector, root);
        }

        // Mark as loaded to prevent timeout error
        window.appLoaded = true;

        console.log("Ahead Booking widget initialized successfully");
      } catch (error) {
        logError("React render", error);
        showEmbedError(
          container,
          "Failed to initialize the booking widget. Please try again later.",
        );
      } finally {
        // Always release the initialization lock when done
        window.isInitializing = false;
        clearTimeout(initTimeout);
      }
    };

    // Add a small delay to ensure DOM is ready
    setTimeout(safeRender, 0);
  } catch (error) {
    logError("initialization", error);
    // Try to show an error message even if initialization fails
    try {
      const container = document.querySelector(selector);
      if (container) {
        showEmbedError(
          container,
          "Failed to initialize booking widget. Please try again later.",
        );
      }
    } catch (displayError) {
      console.error("Error displaying error message:", displayError);
    }
    window.isInitializing = false;
    clearTimeout(initTimeout);
  }
};

// Export the init function to the global scope
window.AheadBooking = {
  init,
};

// Auto-initialize if element with id="ahead-booking" exists
document.addEventListener("DOMContentLoaded", () => {
  const autoMountElement = document.getElementById("ahead-booking");
  if (autoMountElement) {
    console.log("Auto-initializing AheadBooking widget");
    init("#ahead-booking");
  }
});
